import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject, filter, take } from 'rxjs';
import { CountryCode } from 'libphonenumber-js';
import { LegacyReservationStatus } from '../../reservations/reservation-detail/ReservationDetailConstants';
import { getLegacyReservationStatus } from '../../reservations/reservation-detail/ReservationHelper';
import { ReservationService } from '../../reservations/services/reservation.service';
import { GuestService } from '../services/guest.service';
import { GuestVerificationCheckType } from '../models/Guest';
import { VerificationPackagesService } from '../../verification-packages/services/verification-packages.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { DetailPair } from '../../../shared/components/detail-info-card/DetailPair';
import { dateFormat, isCurrent } from '../../../utilities/DateUtility';
import { Reservation } from '../../reservations/models/Reservation';
import { GuestDetails, VerificationResultDetail } from '../models/GuestDetails';
import { DetailInfoCardComponent } from '../../../shared/components/detail-info-card/detail-info-card.component';
import { VerificationDetailsComponent } from './components/verification-details/verification-details.component';
import { GuestDetailVerificationEmailComponent } from './components/guest-detail-verification-email/guest-detail-verification-email.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { PropertyHelper } from '../../properties/shared/PropertyHelper';
import { SafelyResource } from '../../../shared/SafelyResource';
import { formatPhoneNumber } from '../../../utilities/PhoneUtility';

@Component({
  selector: 'app-guest-detail',
  templateUrl: './guest-detail.component.html',
  styleUrls: ['./guest-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    RouterModule,
    VerificationDetailsComponent,
    GuestDetailVerificationEmailComponent,
    DetailInfoCardComponent,
    BackButtonComponent
  ]
})
export class GuestDetailComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  guestId!: number;
  verificationDetails: any[] = [];
  guestDetails: DetailPair[] = [];
  guest!: GuestDetails;
  reservation?: Reservation;
  reservationDetails: DetailPair[] = [];
  verificationPackageCheckTypes = new BehaviorSubject<GuestVerificationCheckType[]>([]);

  shouldDisplayVerificationEmailCard: boolean = false;
  shouldDisplayReservationDetailCard: boolean = false;

  constructor(
    private route: ActivatedRoute, 
    private guestService: GuestService,
    private reservationService: ReservationService,
    public userContextProvider: UserContextProvider,
    private logService: Logger,
    private verificationPackagesService: VerificationPackagesService,
    private readonly propertyHelper: PropertyHelper
  ) { }

  ngOnInit(): void {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.retrieveGuestIdFromUrl();
    })
  }

  retrieveGuestIdFromUrl() {
    this.route.params.subscribe(params => {
      this.guestId = params['id'];
      this.findGuestById(this.guestId);
    });
  }

  async findGuestById(id: number) {
    this.loading.next(true);

    try {
      if (!id) {
        this.logService.handleError('Guest ID is not valid', 'Guest ID is not valid', 'Guest ID is not valid');
        return;
      }

      this.guest = await this.guestService.getGuestDetails(id);
      if (!this.guest || !this.guest.organization_id) {
        this.logService.handleError('An error occurred while retrieving Guest details', 'An error occurred while retrieving Guest details', `An error occurred while retrieving Guest details for ID: ${id}`);
        return;
      } else {
        this.populateVerificationDetails();
        this.populateReservation();
      }
      this.populateGuestDetails();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Guest details', `An error occurred while retrieving Guest details for ID: ${id}`);
    } finally {
      this.loading.next(false);
    }
  }

  async populateVerificationDetails() {
    const results: VerificationResultDetail[] = this.guest.verification_result_details;
    if (results) {
      const verificationPackageId = this.guest.reservation_guest_details[0]?.verification_package_id;
      if (verificationPackageId != null) {
        this.verificationPackageCheckTypes.next(await this.verificationPackagesService.getOrgVerificationPackageCheckTypesById(verificationPackageId));
      }
      this.verificationDetails = results;
    }
  }

  async populateReservation() {
    try {
      const sortedVerificationDetails = this.guest.reservation_guest_details.sort((a, b) => new Date(a.last_update!).getTime() - new Date(b.last_update!).getTime());
      const reservationId = sortedVerificationDetails?.[0].reservation_id;
      if (reservationId) {
        this.reservation = await this.reservationService.getReservationById(reservationId);
        this.shouldDisplayReservationDetailCard = true;

        const reservationIdOptions = {
          id: this.reservation.reservation_id,
          resource: SafelyResource.RESERVATION,
          routeId: this.reservation.reservation_id,
          route: `/reservations/detail/${this.reservation.reservation_id}`
        }

        const reservationReferenceIdOptions = {
          id: this.reservation.reservation_reference_id,
          resource: SafelyResource.RESERVATION,
          routeId: this.reservation.reservation_id,
          route: `/reservations/detail/${this.reservation.reservation_id}`
        }
        const organizationIdOptions = {
          id: this.reservation.organization_id,
          resource: SafelyResource.RESERVATION,
          routeId: this.reservation.organization_id,
          route: `/organizations/detail/${this.reservation.organization_id}`
        }

        this.reservationDetails.push(new DetailPair("Reservation ID", this.reservation.reservation_reference_id, '', reservationReferenceIdOptions, true));
        this.reservationDetails.push(new DetailPair("Safely Reservation ID", this.reservation.reservation_id, '', reservationIdOptions, true));
        this.reservationDetails.push(new DetailPair("Organization ID", this.reservation.organization_id, '', organizationIdOptions, true));
        this.reservationDetails.push(new DetailPair("Organization Name", this.reservation.property.organization_name, '', organizationIdOptions, true));
        this.reservationDetails.push(new DetailPair("Guest Name", `${this.reservation.guests[0].first_name || ''} ${this.reservation.guests[0].last_name || ''}`));
        this.reservationDetails.push(new DetailPair("Property Name", this.reservation.property.property_name));
        this.reservationDetails.push(new DetailPair("Arrival Date", dateFormat(this.reservation.arrival_date, this.userContextProvider.region)));
        this.reservationDetails.push(new DetailPair("Departure Date", dateFormat(this.reservation.departure_date, this.userContextProvider.region)));
        this.reservationDetails.push(new DetailPair("Property Address", await this.propertyHelper.getFormattedPropertyAddress(this.reservation.property)));

        this.configureVerificationEmailCard();
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while loading reservation details for this guest', `Error while retrieving latest Reservation for Guest ID: ${this.guestId}`);
    }
  }

  private configureVerificationEmailCard() {
    if(!this.reservation) {
      this.shouldDisplayVerificationEmailCard = false;
      return;
    }

    this.shouldDisplayVerificationEmailCard = isCurrent(this.reservation.arrival_date) || 
      getLegacyReservationStatus(this.reservation) == LegacyReservationStatus.UPCOMING;
  }

  populateGuestDetails() {
    let dateOfBirth = '';

    if (this.guest.birth_date) {
      try {
        dateOfBirth = formatDate(this.guest.birth_date, 'shortDate', 'en-US', 'UTC');
      } catch (e) {
        this.logService.handleError(e, `Could not convert Guest Date of Birth: ${this.guest.birth_date} to a date`, `Could not convert Guest Date of Birth: ${this.guest.birth_date} to a date`);
      }
    }

    this.guestDetails.push(new DetailPair("Guest ID", this.guest.guest_id));
    this.guestDetails.push(new DetailPair("Full Name", `${this.guest.first_name || ''} ${this.guest.last_name || ''}`));
    this.guestDetails.push(new DetailPair("Phone", formatPhoneNumber(this.guest.phone_number, this.userContextProvider.countryCode as CountryCode)));
    this.guestDetails.push(new DetailPair("Email", this.guest.email));
    this.guestDetails.push(new DetailPair("Date of Birth",  dateOfBirth));

    if (this.guest.address) {
      this.guestDetails.push(new DetailPair("Street Address 1", this.guest.address.address_line_1));
      this.guestDetails.push(new DetailPair("Street Address 2", this.guest.address.address_line_2));
      this.guestDetails.push(new DetailPair("City, State", `${this.guest.address.city}${this.guest.address.city && ', '}${this.guest.address.state || ''}`));
      this.guestDetails.push(new DetailPair("Postal Code", this.guest.address.postal_code));
    }
  }

  refreshGuestDetails() {
    this.findGuestById(this.guestId);
  }
}